import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Página de aterragem para convites de grupo.
 *
 * URL: /invite/:token
 *
 * Comportamento:
 *   - Não autenticado → mostra preview do grupo + botão "Iniciar sessão para aceitar"
 *     (depois do login, volta automaticamente ao convite).
 *   - Autenticado → mostra preview + botão "Aceitar e juntar-me ao grupo".
 *   - Sucesso → toast e redirecciona para /dashboard com toast.
 *   - Convite inválido/expirado/esgotado → mensagem clara.
 */
export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/groups/invite/${token}`)
      .then((res) => {
        setPreview(res.data);
        setError('');
      })
      .catch((e) => {
        setError(e.response?.data?.detail || 'Convite inválido');
        setPreview(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!isAuthenticated) {
      // Guardar destino do convite para voltar após login
      sessionStorage.setItem('post_login_redirect', `/invite/${token}`);
      navigate('/login');
      return;
    }
    setAccepting(true);
    try {
      const res = await axios.post(
        `${API}/groups/invite/${token}/accept`,
        {},
        { withCredentials: true }
      );
      setAccepted(res.data);
      // Redireccionar para dashboard em 2.5s
      setTimeout(() => navigate('/dashboard'), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erro ao aceitar convite');
    } finally {
      setAccepting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-500" data-testid="invite-loading">A carregar convite...</p>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle data-testid="invite-success-title">🎉 Bem-vindo ao grupo!</CardTitle>
            <CardDescription>{accepted.message}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              Já és membro de <strong>{accepted.group_name}</strong>. A redireccionar para o teu painel...
            </p>
            <Button onClick={() => navigate('/dashboard')} className="w-full" data-testid="invite-goto-dashboard">
              Ir para o Painel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle data-testid="invite-error-title">Convite inválido</CardTitle>
            <CardDescription data-testid="invite-error-detail">{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate('/dashboard')} className="w-full">
              Voltar ao Painel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!preview) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-white text-xl font-bold"
            style={{ backgroundColor: preview.color || '#FF2D95' }}
            data-testid="invite-group-color"
          >
            🔒
          </div>
          <CardTitle className="text-center" data-testid="invite-group-name">
            {preview.name}
          </CardTitle>
          <CardDescription className="text-center">
            Foste convidado para te juntares a este grupo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="bg-slate-100 rounded-md p-3 text-sm text-slate-700">
            <p data-testid="invite-member-count">👥 {preview.member_count} membro(s) actual(ais)</p>
            {preview.remaining_uses != null && (
              <p data-testid="invite-remaining-uses">
                ✉️ {preview.remaining_uses} uso(s) restante(s) deste convite
              </p>
            )}
            {preview.expires_at && (
              <p data-testid="invite-expires-at" className="text-xs text-slate-500 mt-1">
                Expira em {new Date(preview.expires_at).toLocaleDateString('pt-PT')}
              </p>
            )}
          </div>
          <Button
            onClick={handleAccept}
            disabled={accepting}
            className="w-full"
            data-testid="invite-accept-button"
          >
            {accepting
              ? 'A aceitar...'
              : isAuthenticated
              ? 'Aceitar e juntar-me ao grupo'
              : 'Iniciar sessão para aceitar'}
          </Button>
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="w-full" data-testid="invite-cancel-button">
            Cancelar
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
